import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import RatingStars from "@/components/base/RatingStars";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { animeList } from "@/mocks/anime";
import { commentList } from "@/mocks/reviews";

export default function AnimeDetailPage() {
  const { id } = useParams();
  const anime = useMemo(() => animeList.find((a) => a.id === id), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState<string | null>(null);

  useDocumentMeta(
    anime ? `${anime.title} - h动漫` : "作品不存在 - h动漫",
    anime ? anime.synopsis : "作品不存在或已下架。"
  );

  const comments = useMemo(() => commentList.filter((c) => c.animeId === id), [id]);
  const related = useMemo(() => {
    if (!anime) return [];
    return animeList
      .filter((a) => a.id !== anime.id && a.genre.some((g) => anime.genre.includes(g)))
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 3);
  }, [anime]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1800);
  };

  if (!anime) {
    return (
      <div className="w-full px-4 md:px-6 py-10">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="作品不存在或已下架"
          description="你访问的作品可能已经下架，去看看其他热门作品吧。"
          actionLabel="返回作品列表"
          onAction={() => {
            window.REACT_APP_NAVIGATE?.("/anime");
          }}
        />
      </div>
    );
  }

  const favorited = isFavorite(anime.id);

  const infoRows = [
    { label: "类型", value: anime.genre.join(" / ") },
    { label: "地区", value: anime.region },
    { label: "年代", value: `${anime.year} 年` },
    { label: "更新状态", value: anime.status },
    { label: "制作公司", value: anime.studio },
    { label: "集数", value: `共 ${anime.episodes} 集` },
  ];

  return (
    <div className="w-full">
      {/* Banner */}
      <div className="relative h-48 md:h-72 bg-foreground-950 overflow-hidden">
        <img
          src={anime.banner}
          alt={anime.title}
          title={`${anime.title} - 动漫作品`}
          className="w-full h-full object-cover object-top opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-black/20 to-black/40" />
      </div>

      <div className="w-full px-4 md:px-6 -mt-20 relative">
        <div className="max-w-5xl mx-auto">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫作品", to: "/anime" }, { label: anime.title }]} />

          <div className="mt-4 flex flex-col md:flex-row gap-5 md:gap-7">
            <div className="w-36 md:w-52 shrink-0 rounded-lg overflow-hidden bg-background-200 border-4 border-background-50 mx-auto md:mx-0">
              <img
                src={anime.cover}
                alt={`${anime.title} 封面`}
                title={`${anime.title} - 动漫作品`}
                className="w-full h-auto object-cover object-top"
              />
            </div>

            <div className="flex-1 min-w-0 pt-2 md:pt-16">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h1 className="font-heading font-extrabold text-foreground-950 text-2xl md:text-4xl leading-snug">
                  {anime.title}
                </h1>
                <button
                  type="button"
                  onClick={() => {
                    const next = toggleFavorite(anime.id);
                    showToast(next ? "已加入收藏（本地保存）" : "已取消收藏");
                  }}
                  className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <i className={favorited ? "ri-heart-fill" : "ri-heart-line"} />
                  {favorited ? "已收藏" : "收藏作品"}
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                <span className="inline-flex items-center gap-1.5 font-semibold text-primary-600 text-base">
                  <i className="ri-star-fill" />
                  {anime.rating.toFixed(1)}
                </span>
                <RatingStars value={anime.rating / 2} />
                <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                  {anime.status}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs">{anime.region}</span>
              </div>

              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{anime.synopsis}</p>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {anime.tags.map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Info table */}
          <section className="mt-6 rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
            <h2 className="font-heading font-semibold text-foreground-950 text-base mb-3">基本信息</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              {infoRows.map((row) => (
                <div key={row.label} className="flex items-center gap-2 text-sm">
                  <dt className="text-foreground-500 shrink-0 w-16">{row.label}</dt>
                  <dd className="text-foreground-900 font-medium">{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
            {/* Main */}
            <div className="min-w-0 space-y-6">
              <section>
                <h2 className="font-heading font-bold text-foreground-950 text-lg mb-3">简介与剧情梗概</h2>
                <div className="space-y-3">
                  {anime.description.map((para, idx) => (
                    <p key={idx} className="text-sm md:text-base text-foreground-700 leading-loose">
                      {para}
                    </p>
                  ))}
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading font-bold text-foreground-950 text-lg">
                    用户评论 <span className="text-foreground-500 text-sm font-normal">（{comments.length}）</span>
                  </h2>
                  <span className="text-xs text-foreground-500">演示示例评论</span>
                </div>
                <div className="space-y-3">
                  {comments.map((c) => (
                    <div key={c.id} className="rounded-lg border border-background-200/70 bg-background-50 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-sm">
                          {c.user.slice(0, 1)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground-900 truncate">{c.user}</p>
                          <p className="text-[11px] text-foreground-500">{c.date}</p>
                        </div>
                        <RatingStars value={c.rating} />
                      </div>
                      <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{c.content}</p>
                      <div className="mt-3 flex items-center gap-1.5 text-xs text-foreground-500">
                        <i className="ri-thumb-up-line" />
                        {c.likes}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Side */}
            <aside className="space-y-4">
              <section className="rounded-lg border border-background-200/70 bg-background-50 p-4">
                <h2 className="font-heading font-semibold text-foreground-950 text-sm mb-3">所属分类</h2>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/anime"
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-tv-2-line" /> 动漫作品
                  </Link>
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-newspaper-line" /> 动漫资讯
                  </Link>
                </div>
              </section>

              {related.length > 0 && (
                <section className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                  <h2 className="px-4 py-3.5 border-b border-background-200/70 font-heading font-semibold text-foreground-950 text-sm">
                    相关推荐
                  </h2>
                  <ul className="p-2">
                    {related.map((r) => (
                      <li key={r.id}>
                        <Link
                          to={`/anime/${r.id}`}
                          className="group flex items-center gap-3 p-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <div className="w-11 h-14 shrink-0 rounded overflow-hidden bg-background-200">
                            <img
                              src={r.cover}
                              alt={r.title}
                              title={r.title}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                              {r.title}
                            </p>
                            <p className="text-[11px] text-foreground-500">
                              {r.genre[0]} · {r.region}
                            </p>
                            <p className="text-[11px] text-primary-600 font-semibold mt-0.5">★ {r.rating.toFixed(1)}</p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </aside>
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}